import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs, getCrumbs, getStat, trainStat } from '../core/save.js';
import { QUESTS, questsForGiver, acceptQuest, turnIn, objectiveProgress, flagObjective } from '../core/quests.js';

// Rice Boi's Dojo — a board-breaking timing minigame.

export function createDojoScene(){
  return () => new DojoScene();
}

const ROUNDS = 5;
const SWEEP_SPEED = 2.4; // seconds for a full sweep

class DojoScene {
  constructor(){
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xE8DCC4);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 13;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildRoom();
    this.rice = this._makeRiceBoi();
    this.scene.add(this.rice.root);

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.dummies = [];
    for (let i = -1; i <= 1; i++){
      const d = this._makeDummy();
      d.position.set(i * 2.2, 0, -2);
      this.scene.add(d);
      this.dummies.push(d);
    }

    this.hotspots = [
      { pos: this.rice.root.position.clone(), r: 2.4, label: '🗣 talk to Rice Boi', action: () => this._talkToRice() },
      { pos: new THREE.Vector3(0, 0, 5.6), r: 2.2, label: '← back to Uptown', action: () => this.manager.goTo('uptown', { from: 'dojo' }) },
      { pos: new THREE.Vector3(-1.5, 0, -2), r: 2.0, label: '🥋 break boards', action: () => this._startGame() }
    ];
    this.nearSpot = null;
    this.panels = [];
    this.state = 'explore';
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.7));
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.8);
    sun.position.set(4, 12, 6); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 16;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _buildRoom(){
    // floor
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), new THREE.MeshLambertMaterial({ color: 0xC9B896 }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    // mats
    for (let x = -5; x <= 5; x += 2.5){
      box(2.0, 0.04, 3.2, 0xA88B6A, x, 0.03, -1, this.scene);
    }
    // back wall scroll
    box(5, 2.4, 0.2, 0xF3E2BE, 0, 1.2, -5.5, this.scene);
    const scroll = textSprite('武', 80, '#4A3020', 1.2, 1.2);
    scroll.position.set(0, 2.2, -5.35); this.scene.add(scroll);
  }

  _makeRiceBoi(){
    const build = (g) => {
      cyl(.5, .32, .42, C.cobalt, 0, .42, 0, g, 20);
      cyl(.48, .48, .06, C.cobaltDim, 0, .63, 0, g, 20);
      sphere(.44, C.cream, 0, .76, 0, g, 16);
      sphere(.12, C.cream, 0, 1.12, 0, g, 10);
      cyl(.03, .03, .16, C.crustDark, 0, 1.24, 0, g, 6);
      box(.13, .035, .03, 0x2E2018, -.15, .86, .38, g).rotation.z = -.4;
      box(.13, .035, .03, 0x2E2018,  .15, .86, .38, g).rotation.z =  .4;
      cyl(.04, .04, .36, C.cream, -.52, .58, .12, g, 6);
      cyl(.04, .04, .36, C.cream,  .52, .58, .12, g, 6);
      cyl(.06, .06, .42, C.cream, -.16, .21, 0, g, 6);
      cyl(.06, .06, .42, C.cream,  .16, .21, 0, g, 6);
    };
    return new Townsperson({
      name: 'Rice Boi', line: 'a warrior must eat.',
      pos: new THREE.Vector3(0, 0, -4), facing: 0, wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _makeDummy(){
    const g = new THREE.Group();
    box(0.7, 1.4, 0.35, 0xD6C49A, 0, 0.7, 0, g);
    box(0.5, 0.4, 0.05, 0xC4B28A, 0, 1.05, 0.18, g);
    return g;
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _talkToRice(){
    this.bread.stop();
    this.bread.drawFace('happy');
    this.riceTab = 'quests';
    this._renderRiceDialog();
  }

  _renderRiceDialog(){
    this._clearPanels();
    const q = questsForGiver('riceboi');
    const activeRows = q.active.map(id => this._questRow(id)).join('');
    const availRows = q.available.map(id => this._questRow(id, true)).join('');
    const body = activeRows || availRows || '<p class="cafe-sub">No training orders right now. Break some boards and come back.</p>';

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Rice Boi</h2>
        <div class="cafe-sub">Way of the Grain Dojo</div>
        <div class="q-tabs">
          <button data-tab="quests" class="cafe-btn ghost ${this.riceTab === 'quests' ? 'active' : ''}">quests</button>
          <button data-tab="tips" class="cafe-btn ghost ${this.riceTab === 'tips' ? 'active' : ''}">tips</button>
        </div>
        <div class="q-tab-body">${this.riceTab === 'quests' ? body : '<p class="cafe-sub">Stop the marker in the green zone for a perfect break. Build combos for bigger payouts.</p>'}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>
    `);
    p.querySelector('[data-tab="quests"]').onclick = () => { this.riceTab = 'quests'; this._renderRiceDialog(); };
    p.querySelector('[data-tab="tips"]').onclick = () => { this.riceTab = 'tips'; this._renderRiceDialog(); };
    p.querySelector('.close').onclick = () => this._clearPanels();
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this._renderRiceDialog(); });
    p.querySelectorAll('.turnin').forEach(b => {
      b.onclick = () => {
        const r = turnIn(b.dataset.id);
        if (r){ HUD.refreshCrumbs(); HUD.refreshQuests(); this.bread.drawFace('yum'); }
        this._renderRiceDialog();
      };
    });
  }

  _questRow(id, accept = false){
    const q = QUESTS[id];
    if (accept) return `<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}</span></div><button class="cafe-btn accept" data-id="${id}">accept</button></div>`;
    const { have, goal, ready } = objectiveProgress(id);
    return `<div class="q-drow ${ready ? 'ready' : ''}"><div><b>${q.title}</b><span>${have}/${goal} ${q.objective.noun}</span></div>${ready ? `<button class="cafe-btn turnin" data-id="${id}">turn in</button>` : ''}</div>`;
  }

  _startGame(){
    if (this.state === 'playing') return;
    this._clearPanels();
    this.state = 'playing';
    this.input.onTap = null;
    HUD.showEnter(null); HUD.setHint(''); HUD.hideHint(); HUD.say('');
    this.bread.stop();
    this.bread.root.position.set(0, 0, 3);
    this.bread.root.rotation.y = Math.PI;

    this.round = 0;
    this.hits = []; // 'perfect' | 'good' | 'miss'
    this.gameTime = 0;
    this.sweepDir = 1;
    this.marker = 0; // 0..1
    this._buildMeter();
  }

  _buildMeter(){
    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card">
        <h2>Board Break</h2>
        <div class="cafe-sub">Round ${this.round + 1} / ${ROUNDS} · Stop the marker in green!</div>
        <div class="dojo-meter">
          <div class="dojo-zone good"></div>
          <div class="dojo-zone perfect"></div>
          <div class="dojo-marker" style="left:0%"></div>
        </div>
        <div class="cafe-stats"><div><b id="dojo-perfect">0</b><span>perfect</span></div><div><b id="dojo-good">0</b><span>good</span></div><div><b id="dojo-miss">0</b><span>miss</span></div></div>
        <button class="cafe-btn" id="dojo-strike">strike!</button>
        <button class="cafe-quit" id="dojo-quit">✕ leave</button>
      </div>
    `);
    this.meterEl = p.querySelector('.dojo-meter');
    this.markerEl = p.querySelector('.dojo-marker');
    p.querySelector('#dojo-strike').onclick = () => this._strike();
    p.querySelector('#dojo-quit').onclick = () => this._teardownGame();
    this._updateStats();
  }

  _updateStats(){
    const p = this.hits.filter(h => h === 'perfect').length;
    const g = this.hits.filter(h => h === 'good').length;
    const m = this.hits.filter(h => h === 'miss').length;
    const pel = document.getElementById('dojo-perfect'); if (pel) pel.textContent = p;
    const gel = document.getElementById('dojo-good'); if (gel) gel.textContent = g;
    const mel = document.getElementById('dojo-miss'); if (mel) mel.textContent = m;
  }

  _strike(){
    let result = 'miss';
    if (this.marker >= 0.42 && this.marker <= 0.58) result = 'perfect';
    else if (this.marker >= 0.20 && this.marker <= 0.80) result = 'good';
    this.hits.push(result);
    this.round++;
    this._updateStats();
    HUD.say(result === 'perfect' ? 'Perfect break!' : result === 'good' ? 'Solid break.' : 'Missed!');

    if (this.round >= ROUNDS){
      this._finishGame();
    } else {
      // tiny pause, rebuild same panel with updated round
      setTimeout(() => this._buildMeter(), 250);
    }
  }

  _finishGame(){
    const perfect = this.hits.filter(h => h === 'perfect').length;
    const good = this.hits.filter(h => h === 'good').length;
    const miss = this.hits.filter(h => h === 'miss').length;
    const score = perfect * 3 + good * 1;
    const strBonus = Math.floor(getStat('str') * 0.5);
    const crumbs = perfect * 4 + good * 2 + strBonus;
    const allPerfect = perfect === ROUNDS;

    if (crumbs > 0) addCrumbs(crumbs);
    HUD.refreshCrumbs();

    // every session hones strength a little
    const strGain = trainStat('str', perfect * 0.2 + good * 0.1);
    if (strGain > 0) HUD.refreshStats();

    flagObjective('dojo-train', 'brokeBoard');
    if (allPerfect) flagObjective('dojo-master', 'perfectRound');

    let grade = 'D';
    if (score >= ROUNDS * 3 - 1) grade = 'S';
    else if (score >= ROUNDS * 2) grade = 'A';
    else if (score >= ROUNDS) grade = 'B';
    else if (score >= ROUNDS * 0.5) grade = 'C';

    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card">
        <div class="cafe-grade">${grade}</div>
        <h2>Training Complete</h2>
        <div class="cafe-sub">${perfect} perfect · ${good} good · ${miss} miss</div>
        <div class="cafe-payout">+${crumbs} crumbs${strGain > 0 ? ` · +${strGain} STR` : ''}</div>
        <button class="cafe-btn" id="dojo-again">train again</button>
        <button class="cafe-btn ghost" id="dojo-leave">leave</button>
      </div>
    `);
    p.querySelector('#dojo-again').onclick = () => this._startGame();
    p.querySelector('#dojo-leave').onclick = () => this._teardownGame();
  }

  _teardownGame(){
    this._clearPanels();
    this.state = 'explore';
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('dojo');
    HUD.say('Back in the dojo. Talk to Rice Boi or break more boards.');
  }

  _tap(ray){
    if (this.state !== 'explore') return;
    if (checkNpcClick(ray, this.rice, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-7, Math.min(7, gp.x));
    gp.z = Math.max(-6, Math.min(5, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.state = 'explore';
    HUD.setSubtitle('Rice Boi\'s Dojo');
    HUD.say('The dojo is quiet except for the snap of practice boards.');
    HUD.setHint('click to walk · talk to Rice Boi or train at the dummies');
    HUD.hideHintSoon(7000);
  }

  exit(){
    this.input.onTap = null;
    HUD.showEnter(null);
    HUD.say('');
    this._clearPanels();
  }

  update(dt){
    this.bread.update(dt);
    this.rice.update(dt, this.bread.root.position);

    if (this.state === 'playing'){
      this.gameTime += dt * this.sweepDir;
      const cycle = (Math.sin(this.gameTime * Math.PI / SWEEP_SPEED) + 1) / 2;
      this.marker = cycle;
      if (this.markerEl) this.markerEl.style.left = `${cycle * 100}%`;
    } else {
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => {
        const d = h.pos.distanceTo(this.bread.root.position);
        if (d < nd){ nd = d; near = h; }
      });
      if (near && nd < near.r){
        const key = near.label;
        if (this.nearSpot !== key){
          this.nearSpot = key;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){
        this.nearSpot = null;
        HUD.showEnter(null);
      }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
